import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { UserContext } from '../context/UserContext';
import { usePaymentStore, usePaymentStoreBase, type Payment } from '../stores/paymentStore';
import { ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, BulkSelectCell, RowActions, RowAction, Amount, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton, btnPrimary, btnDisabled } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import PaymentDialog from '../components/PaymentDialog';
import CustomerLimitBanner from '../components/CustomerLimitBanner';
import { useCustomerLimitCache } from '../hooks/useCustomerLimitCache';
import { bulkDeletePayments, getPayments } from '../services/api';
import { useBulkActions, isAdmin } from '@redpanda/utils';
import { canModify } from '../utils/permissions';
import { notifyDataChanged } from '../utils/dataEvents';
import { useListSearch } from '../hooks/useListSearch';
import { createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler } from '@redpanda/utils';
import { TableSkeleton } from '@redpanda/ui';
import { getCustomerStatus, getArchivedRowClassName } from '../utils/customerUtils';


const PaymentListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const currentUserId = user?.id;
  const userRole = user?.role;
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  
  // 使用客戶限制快取 hook
  const {
    canAddCustomer,
    setCanAddCustomer,
    limitBannerRefreshTrigger,
  } = useCustomerLimitCache();

  const {
    payments,
    pagination,
    error,
    isPageLoading,
    showDeleteConfirmModal,
    isDeleting,
    setCurrentPage,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    fetchPayments,
  } = usePaymentStore();

  // 搜尋由後端查全部資料；沒有搜尋時用列表 store
  const [searchTerm, setSearchTerm] = useState('');
  const search = searchTerm.trim();
  const searchResult = useListSearch<Payment>('payments', search ? { search } : null, page => getPayments(page, { search }));
  const listItems = searchResult.active ? searchResult.items : payments;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => usePaymentStoreBase.getState(), fetchPayments, setCurrentPage);

  // 使用新的批次操作 hook
  const {
    selectedItems: selectedPayments,
    showBulkDeleteModal,
    isBulkDeleting,
    isItemSelected,
    allSelected,
    toggleItemSelection,
    toggleSelectAll,
    handleBulkDelete,
    confirmBulkDelete,
    closeBulkDeleteModal,
    canDelete,
    handlePageChange
  } = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('payments.item', { ns: 'pages' }),
    bulkDeleteApi: bulkDeletePayments,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      usePaymentStoreBase.setState,
      'payment',
      () => payments,
      () => pagination
    ),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      notifyDataChanged('payments');
    },
    userRole,
    t,
    toast,
    // 智能分頁配置
    pagination: pagination ? {
      currentPage: pagination.currentPage,
      totalPages: pagination.totalPages
    } : undefined,
    fetchItems: fetchPayments,
    setCurrentPage
  });

  // 使用 ref 來穩定化函數引用，避免無限循環
  const fetchPaymentsRef = useRef(fetchPayments);
  fetchPaymentsRef.current = fetchPayments;

  useEffect(() => {
    if (isAuthenticated && currentUserId) {
      // 頁面初始化時使用快取（如果有），避免每次切換都重新載入
      fetchPaymentsRef.current(1, false);
    }
  }, [isAuthenticated, currentUserId]);

  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : handlePageChange(page));

  const handleAddPayment = () => {
    setSelectedPayment(null);
    setIsDialogOpen(true);
  };

  const handleEditPayment = (payment: any) => {
    setSelectedPayment(payment);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedPayment(null);
  };

  if (isUserLoading) {
    return null;
  }

  if (!isAuthenticated || !currentUserId) {
    return <div className="p-8 text-center text-red-500">{t('common.messages.unauthorized')}</div>;
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('payments.list.title', { ns: 'pages' })}</h1>
          <div className="flex space-x-3">
            {/* 批量操作按鈕 */}
            {isAdmin(userRole) && (
              <MultiBulkActionButton
                selectedCount={selectedPayments.length}
                isDeleting={isBulkDeleting}
                onDelete={handleBulkDelete}
              />
            )}
            <button 
              onClick={() => canAddCustomer && handleAddPayment()}
              disabled={!canAddCustomer}
              className={canAddCustomer ? btnPrimary : btnDisabled}
              title={canAddCustomer ? undefined : t('customers.list.limitReached', { ns: 'pages' })}
            >
              {t('payments.list.addButton', { ns: 'pages' })}
            </button>
          </div>
        </div>
      </div>

      {/* 客戶數量限制資訊 */}
      <CustomerLimitBanner 
        onLimitChange={setCanAddCustomer} 
        refreshTrigger={limitBannerRefreshTrigger}
      />

      {error && (
        <div className="mb-4 p-4 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-4">
        <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('payments.list.searchPlaceholder', { ns: 'pages' })} />
      </div>

      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {/* 骨架屏載入狀態 */}
      {listLoading ? (
        <TableSkeleton rows={10} columns={7} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <Table style={{ tableLayout: 'fixed', minWidth: '850px' }}>
            <TableHeader>
              <TableRow>
                <BulkSelectHeader
                  userRole={userRole}
                  allSelected={allSelected}
                  onToggleAll={toggleSelectAll}
                  hasItems={listItems.length > 0}
                />
                <TableHead className="w-44">{t('common.table.columns.customer')}</TableHead>
                <TableHead className="w-52">{t('common.table.columns.project')}</TableHead>
                <TableHead numeric className="w-32">{t('common.table.columns.amount')}</TableHead>
                <TableHead className="w-28 pl-10">{t('common.table.columns.paymentMethod')}</TableHead>
                <TableHead numeric className="w-28">{t('common.table.columns.paymentDate')}</TableHead>
                <TableHead className="w-32 pl-10">{t('common.table.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listItems.length === 0 ? (
                <ListEmptyRow
                  colSpan={7}
                  filtered={!!search}
                  title={t('payments.list.noPayments', { ns: 'pages' })}
                  hint={t('payments.list.emptyHint', { ns: 'pages' })}
                  action={canAddCustomer && (
                    <button type="button" onClick={handleAddPayment} className={btnPrimary}>{t('payments.list.addButton', { ns: 'pages' })}</button>
                  )}
                />
              ) : (
                listItems.map((payment) => (
                  <TableRow key={payment.id} className={getArchivedRowClassName(payment.customer)}>
                    <BulkSelectCell
                      userRole={userRole}
                      isSelected={isItemSelected(payment.id)}
                      onToggle={() => toggleItemSelection(payment.id)}
                      canDelete={canDelete()}
                    />
                    <TableCell className="font-medium truncate">
                      {payment.customer?.name || <span className="text-fg-muted">—</span>}
                      {getCustomerStatus(payment.customer).isArchived && (
                        <span className="ml-1 text-[13px] text-fg-secondary">({t('common.archived')})</span>
                      )}
                    </TableCell>
                    <TableCell className="truncate">{payment.project?.name || <span className="text-fg-muted">—</span>}</TableCell>
                    <TableCell numeric><Amount value={payment.amount} /></TableCell>
                    <TableCell className="truncate pl-10">{payment.paymentMethod}</TableCell>
                    <TableCell numeric>{formatTableDate(payment.paymentDate, i18n.language)}</TableCell>
                    <TableCell className="pl-10">
                      <RowActions>
                        {canModify(payment, userRole, currentUserId) && (
                          <>
                            <RowAction onClick={() => handleEditPayment(payment)}>{t('common.actions.edit')}</RowAction>
                            <RowAction tone="danger" onClick={() => handleDelete(payment.id)}>{t('common.actions.delete')}</RowAction>
                          </>
                        )}
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('payments.list.confirmDeleteTitle', { ns: 'pages' })}
        message={t('payments.list.confirmDeleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <PaymentDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        payment={selectedPayment}
      />

      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={closeBulkDeleteModal}
        onConfirm={confirmBulkDelete}
        title={t('common.bulk.confirmDeleteTitle', { count: selectedPayments.length, entity: t('payments.item', { ns: 'pages' }) })}
        message={t('common.bulk.confirmDeleteMessage', { count: selectedPayments.length, entity: t('payments.item', { ns: 'pages' }) })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />
    </div>
  );
};

export default PaymentListPage;